import React, { useRef, useState } from 'react'
import { cn } from '../../lib/utils'

/**
 * WordBlockEditor Component
 * Visual representation of a single word on the timeline
 * Supports selection, dragging, and resizing
 */
const WordBlockEditor = ({
  word,
  wordKey,
  isSelected,
  isCurrent,
  isPast,
  timeToPixels,
  pixelsToTime,
  zoom,
  onSelect,
  onDoubleClick,
  onDrag,
  onResize,
  snapTime
}) => {
  const blockRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeHandle, setResizeHandle] = useState(null) // 'left' or 'right'

  // Safely get timing values with defaults
  const startTime = typeof word?.start_time === 'number' ? word.start_time : 0
  const endTime = typeof word?.end_time === 'number' ? word.end_time : 0

  const startX = timeToPixels(startTime, zoom)
  const endX = timeToPixels(endTime, zoom)
  const width = endX - startX
  const minWidth = 20 // Minimum block width in pixels (smaller for words)

  // Skip rendering if word data is invalid
  if (!word || startTime >= endTime) {
    return null
  }

  // Handle mouse down for dragging the block
  const handleMouseDown = (e) => {
    if (e.button !== 0) return // Only left click
    if (e.target.dataset.handle) return // Ignore if clicking resize handle

    e.preventDefault()
    e.stopPropagation()

    onSelect(wordKey)
    setIsDragging(true)

    const startPageX = e.pageX
    const origStartTime = startTime
    const duration = endTime - startTime

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startPageX
      const deltaTime = pixelsToTime(deltaX, zoom)
      let newStartTime = origStartTime + deltaTime

      // Apply snapping
      newStartTime = snapTime ? snapTime(newStartTime) : newStartTime

      // Ensure within bounds
      newStartTime = Math.max(0, newStartTime)

      const newEndTime = newStartTime + duration

      if (onDrag) {
        onDrag(wordKey, {
          start_time: newStartTime,
          end_time: newEndTime
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Handle mouse down for resizing
  const handleResizeMouseDown = (e, handle) => {
    if (e.button !== 0) return

    e.preventDefault()
    e.stopPropagation()

    onSelect(wordKey)
    setIsResizing(true)
    setResizeHandle(handle)

    const startPageX = e.pageX
    const originalStartTime = startTime
    const originalEndTime = endTime

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startPageX
      const deltaTime = pixelsToTime(deltaX, zoom)

      let newStartTime = originalStartTime
      let newEndTime = originalEndTime

      if (handle === 'left') {
        newStartTime = originalStartTime + deltaTime
        newStartTime = snapTime ? snapTime(newStartTime) : newStartTime
        newStartTime = Math.max(0, newStartTime)

        // Ensure minimum duration
        const minDuration = pixelsToTime(minWidth, zoom)
        if (newEndTime - newStartTime < minDuration) {
          newStartTime = newEndTime - minDuration
        }
      } else if (handle === 'right') {
        newEndTime = originalEndTime + deltaTime
        newEndTime = snapTime ? snapTime(newEndTime) : newEndTime

        // Ensure minimum duration
        const minDuration = pixelsToTime(minWidth, zoom)
        if (newEndTime - newStartTime < minDuration) {
          newEndTime = newStartTime + minDuration
        }
      }

      if (onResize) {
        onResize(wordKey, {
          start_time: newStartTime,
          end_time: newEndTime
        })
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setResizeHandle(null)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Handle double click to edit text
  const handleDoubleClick = () => {
    if (onDoubleClick) {
      onDoubleClick(wordKey)
    }
  }

  return (
    <div
      ref={blockRef}
      className={cn(
        'absolute top-2 bottom-2 rounded cursor-move select-none transition-all',
        'border overflow-hidden',
        isSelected
          ? 'border-purple-400 shadow-lg shadow-purple-500/50 z-20'
          : isCurrent
          ? 'border-purple-300 shadow-md z-10'
          : isPast
          ? 'border-purple-700/40'
          : 'border-purple-500/60',
        isDragging && 'opacity-70 scale-105',
        isResizing && 'opacity-80'
      )}
      style={{
        left: `${startX}px`,
        width: `${Math.max(width, minWidth)}px`,
        background: isSelected
          ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.6), rgba(126, 34, 206, 0.6))'
          : isCurrent
          ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.8), rgba(126, 34, 206, 0.7))'
          : isPast
          ? 'linear-gradient(135deg, rgba(88, 28, 135, 0.4), rgba(76, 29, 149, 0.3))'
          : 'linear-gradient(135deg, rgba(147, 51, 234, 0.4), rgba(126, 34, 206, 0.3))',
        backdropFilter: 'blur(4px)'
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      title={`${word.text || ''} (${startTime.toFixed(2)}s - ${endTime.toFixed(2)}s)`}
    >
      {/* Left resize handle */}
      <div
        data-handle="left"
        className={cn(
          'absolute left-0 top-0 h-full w-1.5 cursor-ew-resize bg-purple-400/0 hover:bg-purple-400/50 transition-colors',
          isResizing && resizeHandle === 'left' && 'bg-purple-400'
        )}
        onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
      />

      {/* Content */}
      <div className="flex items-center justify-center h-full px-1 pointer-events-none">
        <span className={cn(
          'text-xs font-medium truncate',
          isSelected || isCurrent ? 'text-white' : isPast ? 'text-white/40' : 'text-white/70'
        )}>
          {word.text}
        </span>
      </div>

      {/* Right resize handle */}
      <div
        data-handle="right"
        className={cn(
          'absolute right-0 top-0 h-full w-1.5 cursor-ew-resize bg-purple-400/0 hover:bg-purple-400/50 transition-colors',
          isResizing && resizeHandle === 'right' && 'bg-purple-400'
        )}
        onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
      />
    </div>
  )
}

export default WordBlockEditor
